import React from 'react';
import { useNavigate } from 'react-router-dom';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

const ProductivityAnalysis: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Stack spacing={3} sx={{ maxWidth: 860, pb: 4 }}>
      {/* パンくず */}
      <Breadcrumbs separator={<NavigateNextIcon sx={{ fontSize: 16 }} />}>
        <Link
          component="button"
          variant="body2"
          underline="hover"
          color="text.secondary"
          onClick={() => navigate('/devdocs')}
          sx={{ cursor: 'pointer' }}
        >
          Dev Insights
        </Link>
        <Typography variant="body2" color="text.primary">
          AI活用による開発生産性向上の実績分析
        </Typography>
      </Breadcrumbs>

      {/* 記事ヘッダー */}
      <Box>
        <Stack direction="row" spacing={0.5} sx={{ mb: 1.5 }}>
          <Chip
            label="AI"
            size="small"
            variant="outlined"
            sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, borderRadius: 1 }}
          />
          <Chip
            label="Productivity"
            size="small"
            variant="outlined"
            sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, borderRadius: 1 }}
          />
          <Chip
            label="Backend"
            size="small"
            variant="outlined"
            sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, borderRadius: 1 }}
          />
        </Stack>
        <Typography variant="h5" fontWeight={700}>
          AI活用による開発生産性向上の実績分析（バックエンド）
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1.5 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <PersonOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              Hiroki Chigira
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              5 min read
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            2026-08-02
          </Typography>
        </Stack>
      </Box>

      <Divider />

      {/* サマリ */}
      <Section title="サマリ">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          管中バックエンド（Java / Spring Boot）における手動開発とAI開発の工数比較。
          IFPUG簡易法に基づくファンクションポイント算出から工数を見積もった結果。
        </Typography>
        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
          <MetricCard label="総FP" value="679.0" unit="FP" />
          <MetricCard label="総LOC" value="16,906" unit="行" />
          <MetricCard label="ファイル数" value="516" unit="files" />
          <MetricCard label="AI/人 効率比" value="20〜24%" unit="" color="success.main" />
        </Stack>
      </Section>

      {/* 工数比較 */}
      <Section title="工数比較（人月）">
        <DataTable
          headers={['工程', '手動開発', 'AI開発', 'AI/人 比率']}
          rows={[
            ['実装のみ', '6.8人月', '1.4人月', '20%'],
            ['実装 + 設計', '10.9人月', '2.4人月', '22%'],
            ['実装 + 設計 + 試験', '15.3人月', '3.7人月', '24%'],
          ]}
          highlightCol={2}
          chipCol={3}
        />
      </Section>

      {/* 前提条件 */}
      <Section title="前提条件">
        <DataTable
          headers={['項目', '手動開発', 'AI開発']}
          rows={[
            ['生産性', '5.0 FP/人日', '25.0 FP/人日'],
            ['設計工数比率', '実装の60%', '実装の80%'],
            ['試験工数比率', '実装の65%', '実装の90%'],
          ]}
        />
        <Stack spacing={0.5} sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            • 人の生産性: Java/Spring Bootの業務システムでIFPUG標準的な生産性（4〜6
            FP/人日）の中央値
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • AIの生産性:
            構造化度（DD書→コード自動生成の定型パターン、ステアリングルールによる自動判断）を考慮し人の約5倍
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • AI開発の「人日」はAIに指示を出す人間の稼働時間（レビュー・確認・修正指示）を含む
          </Typography>
        </Stack>
      </Section>

      {/* 機能別FP */}
      <Section title="機能別FP・工数内訳">
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography variant="caption" fontWeight={600}>
                    機能
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" fontWeight={600}>
                    FP
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" fontWeight={600}>
                    複雑度
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" fontWeight={600}>
                    手動(人日)
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" fontWeight={600}>
                    AI(人日)
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {functionData.map((row) => (
                <TableRow key={row.name}>
                  <TableCell>
                    <Typography variant="body2">{row.name}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{row.fp}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <ComplexityChip level={row.complexity} />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{row.manDays}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="primary.main" fontWeight={600}>
                      {row.aiDays}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    合計
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600}>
                    679.0
                  </Typography>
                </TableCell>
                <TableCell />
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600}>
                    135.8
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600} color="primary.main">
                    27.2
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </Section>

      {/* LOC集計 */}
      <Section title="ステップ数（LOC）集計">
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography variant="caption" fontWeight={600}>
                    カテゴリ
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" fontWeight={600}>
                    LOC（実効行）
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" fontWeight={600}>
                    ファイル数
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {locData.map((row) => (
                <TableRow key={row.category}>
                  <TableCell>
                    <Typography variant="body2">{row.category}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{row.loc.toLocaleString()}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{row.files}</Typography>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    合計
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600}>
                    16,906
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600}>
                    516
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          ※ LOCは空行・コメント行を除いた実効行数
        </Typography>
      </Section>

      {/* 補足 */}
      <Section title="補足">
        <Stack spacing={0.5}>
          <Typography variant="body2" color="text.secondary">
            •
            契約・契約申請が全体の約40%を占めており、複雑なワークフロー（承認フロー、ステータス遷移、テンプレート管理）がコスト要因
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • 実際のAI開発ではレビュー・手戻り対応で+20〜30%のバッファを見込むのが妥当
          </Typography>
          <Typography variant="body2" color="text.secondary">
            •
            AI開発の設計/試験比率が人より高いのは、DD書の詳細作成・レビュー対応、テストの人によるレビュー・確認が必要な本プロジェクトの特性を反映
          </Typography>
        </Stack>
      </Section>
    </Stack>
  );
};

// --- Sub components ---

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function MetricCard({
  label,
  value,
  unit,
  color,
}: {
  label: string;
  value: string;
  unit: string;
  color?: string;
}) {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
        minWidth: 140,
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Stack direction="row" alignItems="baseline" spacing={0.5}>
        <Typography variant="h6" fontWeight="bold" color={color || 'text.primary'}>
          {value}
        </Typography>
        {unit && (
          <Typography variant="caption" color="text.secondary">
            {unit}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

function DataTable({
  headers,
  rows,
  highlightCol,
  chipCol,
}: {
  headers: string[];
  rows: string[][];
  highlightCol?: number;
  chipCol?: number;
}) {
  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {headers.map((h, i) => (
              <TableCell key={h} align={i > 0 ? 'right' : undefined}>
                <Typography variant="caption" fontWeight={600}>
                  {h}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, ri) => (
            <TableRow key={ri}>
              {row.map((cell, ci) => (
                <TableCell key={ci} align={ci > 0 ? 'right' : undefined}>
                  {chipCol === ci ? (
                    <Chip label={cell} size="small" color="success" variant="outlined" />
                  ) : (
                    <Typography
                      variant="body2"
                      fontWeight={highlightCol === ci ? 600 : undefined}
                      color={highlightCol === ci ? 'primary.main' : undefined}
                    >
                      {cell}
                    </Typography>
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function ComplexityChip({ level }: { level: string }) {
  const colorMap: Record<string, 'success' | 'warning' | 'error'> = {
    低: 'success',
    '低〜中': 'success',
    中: 'warning',
    '中〜高': 'warning',
    高: 'error',
  };
  return (
    <Chip label={level} size="small" color={colorMap[level] || 'default'} variant="outlined" />
  );
}

// --- Data ---

const functionData = [
  { name: '認証', fp: 13.0, complexity: '低', manDays: 2.6, aiDays: 0.5 },
  { name: 'ユーザー管理', fp: 27.0, complexity: '低〜中', manDays: 5.4, aiDays: 1.1 },
  { name: '通知', fp: 57.0, complexity: '中', manDays: 11.4, aiDays: 2.3 },
  { name: 'パートナー', fp: 19.0, complexity: '低', manDays: 3.8, aiDays: 0.8 },
  { name: '要員', fp: 35.0, complexity: '中', manDays: 7.0, aiDays: 1.4 },
  { name: '取引先', fp: 26.0, complexity: '中', manDays: 5.2, aiDays: 1.0 },
  { name: 'プロジェクト', fp: 53.0, complexity: '中〜高', manDays: 10.6, aiDays: 2.1 },
  { name: '契約申請', fp: 160.0, complexity: '高', manDays: 32.0, aiDays: 6.4 },
  { name: '契約', fp: 110.0, complexity: '高', manDays: 22.0, aiDays: 4.4 },
  { name: '区分取得', fp: 13.0, complexity: '低', manDays: 2.6, aiDays: 0.5 },
  { name: '資産管理', fp: 30.0, complexity: '中', manDays: 6.0, aiDays: 1.2 },
  { name: '年度管理', fp: 19.0, complexity: '低〜中', manDays: 3.8, aiDays: 0.8 },
  { name: '検索条件', fp: 16.0, complexity: '低', manDays: 3.2, aiDays: 0.6 },
  { name: 'セキュリティチェック', fp: 76.0, complexity: '中〜高', manDays: 15.2, aiDays: 3.0 },
  { name: '共通基盤', fp: 25.0, complexity: '-', manDays: 5.0, aiDays: 1.0 },
];

const locData = [
  { category: 'UseCase', loc: 5121, files: 79 },
  { category: 'RepositoryImpl', loc: 2815, files: 28 },
  { category: 'DTO + DTO(Model)', loc: 2615, files: 175 },
  { category: 'Mapper', loc: 1900, files: 30 },
  { category: 'Model (全種)', loc: 1085, files: 70 },
  { category: 'Entity', loc: 890, files: 50 },
  { category: 'Controller', loc: 814, files: 19 },
  { category: 'Service', loc: 703, files: 15 },
  { category: 'Repository(I/F)', loc: 337, files: 28 },
  { category: 'その他(Properties/Core/Const等)', loc: 626, files: 22 },
];

export default ProductivityAnalysis;
